import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { links } from '@/utils/link'
import type { AlertFactory } from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import type { JobCreationRootViewModel } from '@/features/jobs/drafting/JobCreationRootViewModel'

/**
 * Dialog to confirm and submit the job of an OCR scope work.
 */
export class OCRConfirmSubmitDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  constructor(
    private readonly _jobCreationRootVm: JobCreationRootViewModel,
    private readonly _navigate: NavigateFn,
    private readonly _newAlert: AlertFactory,
  ) {}

  /**
   * The editor of the job to submit.
   */
  @computed
  get editor() {
    return this._jobCreationRootVm.current
  }

  @computed
  get isBillingRelevant() {
    if (!this.editor.job.owner.isCurrentUser) {
      return false
    }

    return this.editor.submission.billing.isBillingRelevant
  }

  /**
   * Prompts the user to submit the job.
   * @returns Promise resolving to true once submitted, false if canceled
   */
  async prompt(signal?: AbortSignal) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    this.modal.show()

    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  /**
   * Submits the job, keeping the dialog open while submitting.
   */
  submit = task.resolved(async () => {
    await this.editor.submission.submit()

    if (this.editor.submission.submissionStatus.status === 'Success') {
      this._newAlert()
        .saying('Success!', 'Job submitted successfully.')
        .withState('success')
        .autoDismiss()
        .show()
      this._navigate(links.scopingJobs())
    } else {
      this._newAlert()
        .saying(
          'Job submission failed',
          this.editor.submission.submissionStatus.error,
        )
        .withState('error')
        .autoDismiss()
        .show()
    }

    this._prompt.respond(true)
  })
}
